'use client';

import type { FC } from 'react';
import { GiOpenChest, GiLockedChest, GiCrossedSwords, FaAtom, FaGift, FaArrowUp, FaStore, FaTrophy, FaDice, FaGavel, FaUserShield, FaCrosshairs, FaStar, FaBolt, FaQuestionCircle, IoTicket, RouletteDrum } from '../icons';
import type { IconType } from '../icons';
import {
  UserInventoryItemResponseSourceEnum,
  RecentInventoryItemSourceEnum,
} from '../../services/generated/Api';

type SourceType = UserInventoryItemResponseSourceEnum | RecentInventoryItemSourceEnum | string;

interface DropSourceIconProps {
  source: SourceType;
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

/**
 * Maps source types to their corresponding icons
 */
const getIconBySource = (source: SourceType): IconType => {
  switch (source) {
    case UserInventoryItemResponseSourceEnum.CASE_OPENING:
    case RecentInventoryItemSourceEnum.CASE_OPENING:
      return GiOpenChest;
    case UserInventoryItemResponseSourceEnum.CASE_BATTLE:
      return GiCrossedSwords;
    case UserInventoryItemResponseSourceEnum.CONTRACT:
      return FaAtom;
    case UserInventoryItemResponseSourceEnum.GIFT:
    case RecentInventoryItemSourceEnum.GIFT:
      return FaGift;
    case UserInventoryItemResponseSourceEnum.UPGRADE:
      return FaArrowUp;
    case UserInventoryItemResponseSourceEnum.AUCTION:
      return FaGavel;
    case UserInventoryItemResponseSourceEnum.FROM_SHOP:
    case RecentInventoryItemSourceEnum.PURCHASE:
      return FaStore;
    case UserInventoryItemResponseSourceEnum.DRAW:
      return FaDice;
    case UserInventoryItemResponseSourceEnum.CONTEST:
      return FaTrophy;
    case UserInventoryItemResponseSourceEnum.ADMIN_GRANT:
      return FaUserShield;
    case UserInventoryItemResponseSourceEnum.RUSH_MID:
      return FaCrosshairs;
    case UserInventoryItemResponseSourceEnum.DAILY_GIVEAWAY:
      return FaStar;
    case UserInventoryItemResponseSourceEnum.RUSH_RAFFLE:
    case UserInventoryItemResponseSourceEnum.BAND_RAFFLE:
      return IoTicket;
    // Источник ROULETTE — это русская рулетка (`roulette-settlement.service.ts`): револьвер
    // на восемь камор, приз забирает последний выживший. Барабан отвечает и на «колесо»,
    // и на саму механику, а монеты означали бы деньги — тем же `FaCoins` нарисованы кредиты.
    case UserInventoryItemResponseSourceEnum.ROULETTE:
      return RouletteDrum;
    case UserInventoryItemResponseSourceEnum.BAND_CASE:
      return GiLockedChest;
    case UserInventoryItemResponseSourceEnum.BAND_RACE:
      return FaBolt;
    default:
      // Значение с бэкенда, которого мы ещё не знаем, деградирует до заглушки, а не
      // до пустого места: строка без маркера читается как «источник неизвестен нам»,
      // а пустота — как «здесь ничего и не должно быть».
      return FaQuestionCircle;
  }
};

/**
 * Maps source types to their display colors
 */
const getColorBySource = (source: SourceType): string => {
  switch (source) {
    case UserInventoryItemResponseSourceEnum.CASE_OPENING:
    case RecentInventoryItemSourceEnum.CASE_OPENING:
      return '#f59e0b'; // amber - cases
    case UserInventoryItemResponseSourceEnum.CASE_BATTLE:
      return '#ef4444'; // red - battles
    case UserInventoryItemResponseSourceEnum.CONTRACT:
      return '#8b5cf6'; // purple - contracts
    case UserInventoryItemResponseSourceEnum.GIFT:
    case RecentInventoryItemSourceEnum.GIFT:
      return '#ec4899'; // pink - gifts
    case UserInventoryItemResponseSourceEnum.UPGRADE:
      return '#10b981'; // green - upgrades
    case UserInventoryItemResponseSourceEnum.AUCTION:
      return '#3b82f6'; // blue - auctions
    case UserInventoryItemResponseSourceEnum.FROM_SHOP:
    case RecentInventoryItemSourceEnum.PURCHASE:
      return '#6366f1'; // indigo - shop
    case UserInventoryItemResponseSourceEnum.DRAW:
      return '#f97316'; // orange - draws
    case UserInventoryItemResponseSourceEnum.CONTEST:
      return '#eab308'; // yellow - contests
    case UserInventoryItemResponseSourceEnum.ADMIN_GRANT:
      return '#94a3b8'; // slate - admin
    case UserInventoryItemResponseSourceEnum.RUSH_MID:
    case UserInventoryItemResponseSourceEnum.RUSH_RAFFLE:
      return '#22d3ee'; // cyan - семейство Rush
    case UserInventoryItemResponseSourceEnum.DAILY_GIVEAWAY:
      return '#84cc16'; // lime - ежедневный розыгрыш
    case UserInventoryItemResponseSourceEnum.ROULETTE:
      return '#a78bfa'; // violet - рулетка
    // Семейство BAND держим на фирменном розовом клуба (`BandNeon.css`), различая
    // источники глифом: сундук, билет, молния. Так строка отвечает «откуда это»
    // до того, как игрок прочитает подпись.
    case UserInventoryItemResponseSourceEnum.BAND_CASE:
    case UserInventoryItemResponseSourceEnum.BAND_RAFFLE:
    case UserInventoryItemResponseSourceEnum.BAND_RACE:
      return '#ff4aa7'; // pink - SHOT BAND
    default:
      return '#64748b'; // slate - неизвестный источник
  }
};

/**
 * Maps size to font size
 */
const getSizeValue = (size: 'small' | 'medium' | 'large'): number => {
  switch (size) {
    case 'small':
      return 12;
    case 'medium':
      return 16;
    case 'large':
      return 20;
    default:
      return 16;
  }
};

/**
 * Component that displays an icon representing the source of a drop.
 * Supports various source types: CASE_OPENING, CASE_BATTLE, CONTRACT, GIFT, etc.
 */
export const DropSourceIcon: FC<DropSourceIconProps> = ({
  source,
  size = 'medium',
  className,
}) => {
  const Icon = getIconBySource(source);

  const color = getColorBySource(source);
  const fontSize = getSizeValue(size);

  return (
    <span
      className={`inline-flex items-center justify-center leading-none drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)] ${className || ''}`}
      style={{
        color,
        fontSize: `${fontSize}px`,
      }}
    >
      <Icon />
    </span>
  );
};
